import { Building, ClipboardCheck, HandCoins, Home, Landmark, Network } from "lucide-react";
import { IconCard, Notice, PageHero, SectionHead } from "../components/site/Blocks";
import { PartnerForm } from "../components/forms/PartnerForm";

const WHO = [
  [ClipboardCheck, "Case managers & social workers", "Professionals looking for in-home or housing support options for the people they serve."],
  [Home, "Housing organizations", "Housing providers and programs interested in collaborating on appropriate accommodation."],
  [Network, "Community agencies", "Community and social service organizations supporting adults and families."],
  [Building, "Property owners & landlords", "Have a home or rental property? Rent it to us and we will run it as a housing program for the people we serve."],
  [Landmark, "Property managers", "Manage rental homes? Lease one to us for use as a housing program."],
  [HandCoins, "Other partners", "Organizations and individuals who share our commitment to accessible care and housing."],
];
const TOPICS = ["Care referrals", "Housing opportunities", "Property arrangements", "Community partnerships"];

export default function Partner() {
  return (
    <>
      <PageHero eyebrow="Partner With Us" title="Working together to connect people with care and housing." image="/images/partner.jpg" alt="Two professionals shaking hands" testId="partner-hero"
        actions={<a href="#partner-inquiry" className="btn-pill btn-primary" data-testid="partner-start-cta-button">Start a partnership inquiry</a>}>
        <p>Friendship Pen Care Services welcomes conversations about care referrals, housing opportunities, property arrangements and community partnerships. If your work touches the lives of adults who need care or stable housing, we would be glad to hear from you.</p>
      </PageHero>
      <section className="container-site pt-16 sm:pt-24" data-testid="partner-rent-your-home">
        <div className="rounded-[2rem] bg-[#1E3A2B] text-[#FAF8F5] p-8 sm:p-12 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <span className="eyebrow !text-[#F5E6A3]">For property owners</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Rent your home to us.</h2>
            <p className="text-[#E5E0D8] mt-4 leading-relaxed">If you own a home or rental property, you can rent it to Friendship Pen Care Services. We turn it into a housing program for the people we serve.</p>
          </div>
          <ol className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {[["You rent us your property", "You lease your home or rental property to us."], ["We run a housing program", "We use it to house people from the groups we serve."], ["We agree on the terms", "Lease terms are discussed and agreed with you directly."]].map(([t, d], i) => (
              <li key={t} className="rounded-2xl border border-white/15 bg-white/5 p-5">
                <span className="font-display text-3xl text-[#F5E6A3]">0{i + 1}</span>
                <p className="font-semibold mt-3">{t}</p>
                <p className="text-sm text-[#E5E0D8] mt-1">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section className="container-site py-16 sm:py-24">
        <SectionHead eyebrow="Who we work with" title="We welcome inquiries from a range of partners." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {WHO.map(([i, t, d]) => <IconCard key={t} icon={i} title={t} testId={`partner-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{d}</IconCard>)}
        </div>
        <div className="mt-12 flex flex-wrap gap-3">
          {TOPICS.map((t) => <span key={t} className="rounded-full bg-[#F3EFEA] border border-[#E5E0D8] px-5 py-2 text-sm font-medium text-[#1E3A2B]">{t}</span>)}
        </div>
      </section>
      <section id="partner-inquiry" className="bg-[#F3EFEA] border-t border-[#E5E0D8] scroll-mt-24">
        <div className="container-site py-16 sm:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-6">
            <SectionHead eyebrow="Partnership inquiry" title="Tell us about the collaboration you have in mind." />
            <Notice testId="partner-notice">Submitting this form starts a conversation. It does not create a referral arrangement, property agreement or formal partnership.</Notice>
          </div>
          <div className="lg:col-span-8"><PartnerForm /></div>
        </div>
      </section>
    </>
  );
}
